import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PerformanceDocTableRow from './PerformanceDocTableRow'
import PerformanceDocsAddPopup from '../PerformanceDocsAddPopup'
import PerformanceDocsEditPopup from '../PerformanceDocsEditPopup'

import PerformancePaymentTableRow from './PerformancePaymentTableRow'
import PerformancePaymentsAddPopup from '../PerformancePaymentsAddPopup'

import { ContractPosition } from '../../../../types'

import {
    ContractPerformanceDoc,
    PerformanceContractDocPositionDraft,
    PerformanceContractDocPositionEdit,
    ContractPerformancePayment,
    ContractPerformancePaymentInit,
    PerformanceDocsError,
} from '../../types'

import LoadingIcon from '../../../../../../../components/LoadingIcon'

import { CurrencyList } from '../../../types'
import HoverTooltip from '../../../../../../../components/HoverTooltip'

interface PerformanceCurrency {
    currencyCharCode: string | null
    currencyName: string | null
    currencyDigitalCode: number | null
}

const initialDraft: PerformanceContractDocPositionDraft = {
    performance_contract_doc_date: null,
    performance_contract_doc_code: null,
    performance_contract_doc_name: null,
    performance_contract_doc_number: null,
    prepayment: false,
    currency_code: null,
    currency_digital_code: null,
    currency_name: null,
    exchange_rate: null,
    exchange_rate_date: null,
    performance_contract_doc_position_item: [],
}

type PerfTotalDocSum = {
    total_doc: number
    total_doc_rub: number
}

type Props = {
    theme: string
    perfAction: boolean
    performanceGuid: string | undefined
    contractGuid: string | undefined
    contractRegNumber: string | undefined
    currencyList: CurrencyList[]
    performanceCurrency?: PerformanceCurrency
    onPerformanceDocs: (have_perf_docs: boolean) => void
    errors?: PerformanceDocsError
    onAnyEdit: () => void
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PerformanceDocTable: React.FC<Props> = (props) => {
    const navigate = useNavigate()

    const [performanceDocs, setPerformanceDocs] = useState<
        ContractPerformanceDoc[] | null
    >(null)
    const [isLoadingPerformanceDocs, setLoadingPerformanceDocs] =
        useState(false)

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [performanceTotalDocSum, setPerformanceTotalDocSum] =
        useState<PerfTotalDocSum | null>()
    const [isLoadingPerformanceTotalDocSum, setLoadingPerformanceTotalDocSum] =
        useState(false)

    const [performanceDocsAddPopup, setPerformanceDocsAddPopup] =
        useState<boolean>(false)
    const [performanceDocsEditPopup, setPerformanceDocsEditPopup] =
        useState<boolean>(false)

    const [
        isLoadingPerformanceDocsPositionDraft,
        setLoadingPerformanceDocsPositionDraft,
    ] = useState(false)
    const [
        performanceContractDocsPositionDraft,
        setPerformanceContractDocsPositionDraft,
    ] = useState<PerformanceContractDocPositionDraft>(initialDraft)

    const [
        isLoadingPerformanceDocsPositionEdit,
        setLoadingPerformanceDocsPositionEdit,
    ] = useState(false)
    const [
        performanceContractDocsPositionEdit,
        setPerformanceContractDocsPositionEdit,
    ] = useState<PerformanceContractDocPositionEdit | null>(null)

    const [deletePerformancePositionPopup, setDeletePerformancePositionPopup] =
        useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)

    const [
        deleteRowPerformancePositionPopup,
        setDeleteRowPerformancePositionPopup,
    ] = useState<boolean>(false)
    const [deleteRowGuid, setDeleteRowGuid] = useState<string>('')

    const [isSavingPerformanceDoc, setIsSavingPerformanceDoc] = useState(false)

    const loadPerformanceContractDocs = () => {
        setLoadingPerformanceDocs(true)

        fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/performance_contract_doc/grouped/performance_contract_guid/${props.performanceGuid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    props.onPerformanceDocs(false)
                    throw new Error('Failed to fetch count')
                }
            })
            .then((data) => {
                // Обновляем состояния
                setPerformanceDocs(data)
                props.onPerformanceDocs(true)
            })
            .catch((e) => {
                console.error(e)
                // Устанавливаем значения по умолчанию на случай ошибки
                setPerformanceDocs(null)
                props.onPerformanceDocs(false)
            })
            .finally(() => {
                setLoadingPerformanceDocs(false)
            })
    }

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (perfDoc_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDoc_position_guid)) {
                return prevSelectedItems.filter(
                    (itemId) => itemId !== perfDoc_position_guid
                )
            } else {
                return [...prevSelectedItems, perfDoc_position_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (perfDoc_position_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(perfDoc_position_guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, perfDoc_position_guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    // const handleSelectAllChange = () => {
    //     setSelectAll(!selectAll)
    //     if (!selectAll) {
    //         // Выбираем только те элементы, которые соответствуют текущей стадии
    //         const filteredItems = performanceDocs
    //             ? performanceDocs.map(
    //                   (item) => item.performance_contract_position_guid
    //               )
    //             : []

    //         setSelectedItems(filteredItems)
    //     } else {
    //         // Сбрасываем выбор всех элементов
    //         setSelectedItems([])
    //     }
    // }

    const loadPerformanceTotalDocSum = async () => {
        setLoadingPerformanceTotalDocSum(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/performance_contract_guid/total_doc/${props.performanceGuid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceTotalDocSum(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerfTotalDocSum = await response.json()
            setPerformanceTotalDocSum(json)
        } catch (e) {
            console.error('Error fetching doc sum:', e)
            setPerformanceTotalDocSum(null)
        } finally {
            setLoadingPerformanceTotalDocSum(false)
        }
    }

    const loadPerformanceContractDocsPositionDraft = async () => {
        setLoadingPerformanceDocsPositionDraft(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/draft_from_contract?contract_guid=${props.contractGuid}&contract_reg_number=${props.contractRegNumber}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceContractDocsPositionDraft(initialDraft)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerformanceContractDocPositionDraft =
                await response.json()
            setPerformanceContractDocsPositionDraft(json)
        } catch (e) {
            console.error('Error fetching draft:', e)
            setPerformanceContractDocsPositionDraft(initialDraft)
        } finally {
            setLoadingPerformanceDocsPositionDraft(false)
        }
    }

    const loadPerformanceContractDocsPositionEdit = async (
        performance_contract_doc_guid: string
    ) => {
        setLoadingPerformanceDocsPositionEdit(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/edit_payload/${performance_contract_doc_guid}`
        )
        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setPerformanceContractDocsPositionEdit(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: PerformanceContractDocPositionEdit =
                await response.json()
            setPerformanceContractDocsPositionEdit(json)
        } catch (e) {
            console.error('Error fetching payment sum:', e)
            setPerformanceContractDocsPositionEdit(null)
        } finally {
            setLoadingPerformanceDocsPositionEdit(false)
        }
    }

    const handlePerformanceDocsEditPopup = (
        performance_contract_doc_guid: string
    ) => {
        const willOpen = !performanceDocsEditPopup
        setPerformanceDocsEditPopup(willOpen)
        if (willOpen) {
            loadPerformanceContractDocsPositionEdit(
                performance_contract_doc_guid
            )
        } else {
            setPerformanceContractDocsPositionEdit(null)
        }
    }

    const handlePerformanceDocsAddPopup = () => {
        setPerformanceDocsAddPopup(!performanceDocsAddPopup)
        if (!performanceDocsAddPopup == true) {
            loadPerformanceContractDocsPositionDraft()
        } else {
            setPerformanceContractDocsPositionDraft(initialDraft)
        }
    }

    const deletePerformanceContractPosition = async (guid: string) => {
        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_position/${guid}`

        const res = await fetch(url, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        if (!res.ok) {
            // Пытаемся прочитать сообщение об ошибке
            let errText = ''
            try {
                const data = await res.json()
                errText = data?.detail || data?.message || JSON.stringify(data)
            } catch {
                try {
                    errText = await res.text()
                } catch {
                    // no-op
                }
            }
            throw new Error(
                `DELETE ${guid} failed: ${res.status} ${res.statusText}${errText ? ' — ' + errText : ''}`
            )
        }
    }

    const handleDeletePerformancePositionPopup = () => {
        setDeletePerformancePositionPopup(!deletePerformancePositionPopup)
    }

    const handleDeleteRowPerformancePositionPopup = () => {
        setDeleteRowPerformancePositionPopup(!deleteRowPerformancePositionPopup)
    }

    const handleDeleteClick = async () => {
        setDeleting(true)

        try {
            if (!selectedItems.length) {
                // Нечего удалять
                return
            }

            // Параллельно отправляем DELETE по каждому guid
            const results = await Promise.allSettled(
                selectedItems.map((guid) =>
                    deletePerformanceContractPosition(guid)
                )
            )

            // Разбираем успехи/ошибки
            const failed: { guid: string; error: string }[] = []
            results.forEach((r, idx) => {
                const guid = selectedItems[idx]
                if (r.status === 'rejected') {
                    const msg =
                        r.reason instanceof Error
                            ? r.reason.message
                            : String(r.reason)
                    failed.push({ guid, error: msg })
                }
            })

            // В любом случае обновляем данные
            await Promise.allSettled([
                loadPerformanceContractDocs(),
                loadPerformanceTotalDocSum(),
            ])
        } finally {
            setDeleting(false)
            setSelectedItems([])
            setSelectAll(false)
            setDeletePerformancePositionPopup(false)
            setDeleteRowPerformancePositionPopup(false)
            props.onAnyEdit()
        }
    }

    // основная функция сохранения (аналог твоих load*)
    const createPerformanceContractDocNew = async (
        payload: PerformanceContractDocPositionDraft
    ) => {
        setIsSavingPerformanceDoc(true)

        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc_new?performance_contract_guid=${props.performanceGuid}`

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            if (response.ok) {
                console.log('Документ успешно создан')
                loadPerformanceContractDocs()
                loadPerformanceTotalDocSum()
            } else {
                console.log('Ошибка! Документ не создан')
            }
        } catch (e) {
            console.log('Ошибка! Документ не создан')
        } finally {
            setIsSavingPerformanceDoc(false)
            props.onAnyEdit()
        }
    }

    const savePerformanceContractDoc = async (
        payload: PerformanceContractDocPositionEdit | null
    ) => {
        setIsSavingPerformanceDoc(true)

        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/performance_contract_doc/edit_payload/${performanceContractDocsPositionEdit?.performance_contract_position_guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            if (response.ok) {
                console.log('Документ успешно сохранен')
                loadPerformanceContractDocs()
                loadPerformanceTotalDocSum()
            } else {
                console.log('Ошибка! Документ не сохранен')
            }
        } catch (e) {
            console.log('Ошибка! Документ не сохранен')
        } finally {
            setIsSavingPerformanceDoc(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (props.performanceGuid) {
                    loadPerformanceContractDocs()
                    loadPerformanceTotalDocSum()
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    const n = (v: unknown, def = 0) =>
        v === null || v === undefined || v === '' ? def : Number(v)

    const isRUB = (digital?: number | null) => Number(digital) === 643

    const reindexOrdinals = <T extends { ordinal: number }>(arr: T[]): T[] =>
        arr.map((item, idx) => ({ ...item, ordinal: idx + 1 }))

    const onUpdateEditItem = (
        ordinal: number,
        patch: Partial<
            PerformanceContractDocPositionEdit['performance_contract_doc_position_item'][number]
        >
    ) => {
        setPerformanceContractDocsPositionEdit((prev) => {
            if (!prev) return prev
            const items = prev.performance_contract_doc_position_item ?? []
            const idx = items.findIndex((x) => x.ordinal === ordinal)
            if (idx === -1) return prev
            const next = [...items]
            next[idx] = { ...next[idx], ...patch }
            return { ...prev, performance_contract_doc_position_item: next }
        })
    }

    const onRemoveEditItems = (ordinals: number[]) => {
        setPerformanceContractDocsPositionEdit((prev) => {
            if (!prev) return prev
            const items = prev.performance_contract_doc_position_item ?? []
            const kept = items.filter((x) => !ordinals.includes(x.ordinal))
            const reindexed = reindexOrdinals(kept)
            return {
                ...prev,
                performance_contract_doc_position_item: reindexed,
            }
        })
    }

    const onAddEditItems = (positions: ContractPosition[]) => {
        setPerformanceContractDocsPositionEdit((prev) => {
            if (!prev) return prev

            const items = prev.performance_contract_doc_position_item ?? []
            const next = [...items]

            const exch = n(prev.exchange_rate, 0)
            const rubHeader = isRUB(prev.currency_digital_code)

            for (const p of positions) {
                const qty = n(p.qty, 0)
                const unit_price = n(p.unit_price, 0)

                const rub_unit_price = rubHeader
                    ? unit_price
                    : unit_price * exch
                const total_rub = rubHeader
                    ? unit_price * qty
                    : unit_price * qty * exch

                next.push({
                    performance_contract_doc_guid: null,
                    ordinal: next.length + 1,

                    contract_position_guid: p.guid ?? '',
                    contract_position_name: p.contract_position_name,
                    contract_position_okpd2_code: p.okpd2_code,
                    contract_position_okpd2_name: p.okpd2_name,
                    contract_position_type_object_purchase:
                        p.type_object_purchase,
                    contract_position_country_of_origin:
                        p.country_of_origin ?? null,
                    contract_position_country_manufacturer:
                        p.country_manufacturer ?? null,
                    contract_position_type_provision: p.type_provision ?? null,
                    contract_position_contract_num: p.contract_num ?? null,
                    contract_position_contract_end_stage:
                        p.contract_end_stage ?? null,
                    contract_position_target_code: p.target_code ?? null,
                    contract_position_type_code: p.type_code ?? null,
                    contract_position_amount_of_budget_funds: n(
                        p.amount_of_budget_funds,
                        0
                    ),

                    long_term_contract_volume_detail_info_month: null,
                    long_term_contract_volume_detail_info_year: null,

                    okei_code: p.okei_code ?? null,
                    okei_name: p.okei_name ?? null,
                    qty: qty,
                    unit_price: unit_price,

                    rub_unit_price: rub_unit_price,
                    total_rub: total_rub,

                    annotation: null,
                })
            }

            return { ...prev, performance_contract_doc_position_item: next }
        })
    }

    const onUpdateDraftItem = (
        ordinal: number,
        patch: Partial<
            PerformanceContractDocPositionDraft['performance_contract_doc_position_item'][number]
        >
    ) => {
        setPerformanceContractDocsPositionDraft((prev) => {
            if (!prev) return prev
            const items = prev.performance_contract_doc_position_item ?? []
            const idx = items.findIndex((x) => x.ordinal === ordinal)
            if (idx === -1) return prev
            const next = [...items]
            next[idx] = { ...next[idx], ...patch }
            return { ...prev, performance_contract_doc_position_item: next }
        })
    }

    const onRemoveDraftItems = (ordinals: number[]) => {
        setPerformanceContractDocsPositionDraft((prev) => {
            if (!prev) return prev
            const items = prev.performance_contract_doc_position_item ?? []
            const kept = items.filter((x) => !ordinals.includes(x.ordinal))
            const reindexed = reindexOrdinals(kept)
            return {
                ...prev,
                performance_contract_doc_position_item: reindexed,
            }
        })
    }

    const onAddDraftItems = (positions: ContractPosition[]) => {
        setPerformanceContractDocsPositionDraft((prev) => {
            if (!prev) return prev

            const items = prev.performance_contract_doc_position_item ?? []
            const next = [...items]

            const exch = n(prev.exchange_rate, 0)
            const rubHeader = isRUB(prev.currency_digital_code)

            for (const p of positions) {
                const qty = n(p.qty, 0)
                const unit_price = n(p.unit_price, 0)

                const rub_unit_price = rubHeader
                    ? unit_price
                    : unit_price * exch
                const total_rub = rubHeader
                    ? unit_price * qty
                    : unit_price * qty * exch

                next.push({
                    ordinal: next.length + 1,

                    contract_position_guid: p.guid ?? '',
                    contract_position_name: p.contract_position_name,
                    contract_position_okpd2_code: p.okpd2_code,
                    contract_position_okpd2_name: p.okpd2_name,
                    contract_position_type_object_purchase:
                        p.type_object_purchase,
                    contract_position_country_of_origin:
                        p.country_of_origin ?? null,
                    contract_position_country_manufacturer:
                        p.country_manufacturer ?? null,
                    contract_position_type_provision: p.type_provision ?? null,
                    contract_position_contract_num: p.contract_num ?? null,
                    contract_position_contract_end_stage:
                        p.contract_end_stage ?? null,
                    contract_position_target_code: p.target_code ?? null,
                    contract_position_type_code: p.type_code ?? null,
                    contract_position_amount_of_budget_funds: n(
                        p.amount_of_budget_funds,
                        0
                    ),

                    long_term_contract_volume_detail_info_month: null,
                    long_term_contract_volume_detail_info_year: null,

                    okei_code: p.okei_code ?? null,
                    okei_name: p.okei_name ?? null,
                    qty: qty,
                    unit_price: unit_price,

                    rub_unit_price: rub_unit_price,
                    total_rub: total_rub,
                })
            }

            return { ...prev, performance_contract_doc_position_item: next }
        })
    }

    return (
        <>
            <fieldset disabled={false}>
                <HoverTooltip tooltipText={props.errors?.have_docs_items || ''}>
                    <div
                        className={`flex flex-col w-full min-w-fit bg-[#e4e6e8] dark:bg-[#242225] rounded ${
                            props.errors?.have_docs_items
                                ? 'border border-[#C15C5C]'
                                : ''
                        }`}
                    >
                        {/* toolbar */}
                        <div className="flex flex-row gap-1 p-[5px] h-[40px] text-[10px] rounded-t text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                            {props.perfAction ? (
                                ''
                            ) : (
                                <>
                                    <div className="flex w-8 h-full items-center justify-center text-center">
                                        {/* Добавить документ*/}
                                        <button
                                            onClick={
                                                handlePerformanceDocsAddPopup
                                            }
                                            className="w-6 h-6 grid rounded-full items-center justify-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        >
                                            <HoverTooltip tooltipText="Добавить документ">
                                                <svg
                                                    className="w-4 h-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="plus"
                                                >
                                                    <path
                                                        fill={
                                                            props.theme ==
                                                            'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                        }
                                                        d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                    ></path>
                                                </svg>
                                            </HoverTooltip>
                                        </button>
                                    </div>
                                    <div className="flex w-8 h-full items-center justify-center text-center">
                                        {/* Удалить */}
                                        <button
                                            onClick={
                                                handleDeletePerformancePositionPopup
                                            }
                                            className={`flex w-6 h-6 rounded-full items-center justify-center ${selectedItems.length > 0 ? 'hover:bg-[#FFFFFF] dark:hover:bg-[#656365]' : ''} `}
                                            disabled={
                                                selectedItems.length > 0
                                                    ? false
                                                    : true
                                            }
                                        >
                                            <HoverTooltip tooltipText="Удалить документ">
                                                <svg
                                                    className="w-4 h-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    id="plus"
                                                >
                                                    <path
                                                        fill={`${
                                                            selectedItems.length >
                                                            0
                                                                ? props.theme ==
                                                                  'dark'
                                                                    ? '#FFFFFF'
                                                                    : '#181818'
                                                                : '#8A8A8A'
                                                        } `}
                                                        d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                    ></path>
                                                </svg>
                                            </HoverTooltip>
                                        </button>
                                    </div>
                                </>
                            )}

                            <div className="flex flex-1 h-full items-center justify-center text-center"></div>

                            <div className="flex flex-col w-[215px] h-full gap-1 items-center justify-center text-center">
                                <div className="flex w-full h-fit items-center justify-end text-right">
                                    Сумма приемки (₽)
                                    <br />
                                    {isLoadingPerformanceTotalDocSum ? (
                                        <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                                            <svg
                                                className={`animate-spin h-4 w-4 ${
                                                    props.theme === 'dark'
                                                        ? 'text-[#FFFFFF]'
                                                        : 'text-[#181818]'
                                                }`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        </div>
                                    ) : performanceTotalDocSum?.total_doc_rub ? (
                                        Number(
                                            performanceTotalDocSum?.total_doc_rub
                                        ).toLocaleString('ru-RU', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 13,
                                        })
                                    ) : (
                                        performanceTotalDocSum?.total_doc_rub
                                    )}
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>
                        {/* name */}
                        <div className="flex flex-row gap-1 h-[40px] p-[5px] items-center justify-center text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#2a282b]">
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-[40px] h-full items-center justify-center text-center">
                                №
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center">
                                Наименование документа
                            </div>
                            <div className="flex flex-col w-[215px] h-full items-center justify-center text-center">
                                <div className="flex flex-row w-[215px] h-[25px] items-center justify-center text-center">
                                    <div className="flex w-[95px] h-full items-center justify-center text-center">
                                        Дата
                                    </div>
                                    <div className="flex w-[120px] h-full items-center justify-center text-center">
                                        Сумма
                                    </div>
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>

                        {/* date */}
                        <div className="flex flex-col w-full gap-1 my-1 overflow-y custom-scrollbar">
                            {isLoadingPerformanceDocs ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : performanceDocs ? (
                                performanceDocs.map((row, index) => (
                                    <PerformanceDocTableRow
                                        key={row.unic_code}
                                        perfAction={props.perfAction}
                                        performanceGuid={props.performanceGuid}
                                        perfDocNumber={index + 1}
                                        theme={props.theme}
                                        performanceDoc={row}
                                        currencyList={props.currencyList}
                                        selected={selectedItems.includes(
                                            row.performance_contract_position_guid
                                        )}
                                        onClick={(perfDoc_position_guid) => {
                                            handleCheckboxChange(
                                                perfDoc_position_guid
                                            )
                                        }}
                                        onRightClick={(
                                            perfDoc_position_guid
                                        ) => {
                                            handleRightClickCheckboxChange(
                                                perfDoc_position_guid
                                            )
                                        }}
                                        onDeleteRow={(
                                            perfDoc_position_guid
                                        ) => {
                                            setSelectedItems([])
                                            setDeleteRowGuid(
                                                perfDoc_position_guid
                                            )
                                            handleDeleteRowPerformancePositionPopup()
                                        }}
                                        onClickEdit={(perfDoc_position_guid) =>
                                            handlePerformanceDocsEditPopup(
                                                perfDoc_position_guid
                                            )
                                        }
                                    />
                                ))
                            ) : (
                                <div className="flex w-full h-full py-1 items-center justify-center text-[12.8px] text-[#181818] dark:text-[#FFFFFF]">
                                    Нет данных
                                </div>
                            )}
                        </div>
                        {/* buttons */}
                        <div className="flex flex-row gap-1 h-[40px] text-[10px] p-1 rounded-b text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#dbdbdb] dark:bg-[#333033]">
                            <div className="flex flex-1 flex-row gap-3 px-5 h-full items-center justify-start text-center">
                                {!props.perfAction && (
                                    <>
                                        <button
                                            className="flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                            disabled={false}
                                            onClick={
                                                handlePerformanceDocsAddPopup
                                            }
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 22 22"
                                                id="plus"
                                            >
                                                <path
                                                    fill={`${props.theme == 'dark' ? '#FFFFFF' : '#181818'}`}
                                                    d="M19,11H13V5a1,1,0,0,0-2,0v6H5a1,1,0,0,0,0,2h6v6a1,1,0,0,0,2,0V13h6a1,1,0,0,0,0-2Z"
                                                ></path>
                                            </svg>
                                            Добавить
                                        </button>
                                        <button
                                            className={`flex flex-row gap-2 px-4 py-1 min-w-[120px] items-center justify-center rounded-[10px] text-xs ${selectedItems.length > 0 ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] hover:bg-[#ed9cae] dark:hover:bg-[#701f1f] bg-[#f6f6f6] dark:bg-[#494849]' : 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#f6f6f6] dark:bg-[#494849]'} `}
                                            disabled={
                                                !(selectedItems.length > 0)
                                            }
                                            onClick={
                                                handleDeletePerformancePositionPopup
                                            }
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 22 22"
                                                id="trash"
                                            >
                                                <path
                                                    fill={
                                                        selectedItems.length > 0
                                                            ? props.theme ==
                                                              'dark'
                                                                ? '#FFFFFF'
                                                                : '#181818'
                                                            : props.theme ==
                                                                'dark'
                                                              ? '#8A8A8A'
                                                              : '#6A6A6A'
                                                    }
                                                    d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                ></path>
                                            </svg>
                                            Удалить
                                        </button>
                                    </>
                                )}
                            </div>
                            <div className="flex flex-1 h-full items-center justify-center text-center"></div>

                            <div className="flex flex-col w-[215px] h-full gap-1 items-center justify-center text-center">
                                <div className="flex w-full h-fit items-center justify-end text-right">
                                    Сумма приемки (₽)
                                    <br />
                                    {isLoadingPerformanceTotalDocSum ? (
                                        <div className="flex w-8 items-center justify-center text-center text-[#8A8A8A] ">
                                            <svg
                                                className={`animate-spin h-4 w-4 ${
                                                    props.theme === 'dark'
                                                        ? 'text-[#FFFFFF]'
                                                        : 'text-[#181818]'
                                                }`}
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        </div>
                                    ) : performanceTotalDocSum?.total_doc_rub ? (
                                        Number(
                                            performanceTotalDocSum?.total_doc_rub
                                        ).toLocaleString('ru-RU', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 13,
                                        })
                                    ) : (
                                        performanceTotalDocSum?.total_doc_rub
                                    )}
                                </div>
                            </div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                            <div className="flex w-8 h-full items-center justify-center text-center"></div>
                        </div>
                    </div>
                </HoverTooltip>
            </fieldset>

            {performanceDocsAddPopup && (
                <PerformanceDocsAddPopup
                    theme={props.theme}
                    performanceGuid={props.performanceGuid}
                    currencyList={props.currencyList}
                    handlePerformanceDocsAddPopup={
                        handlePerformanceDocsAddPopup
                    }
                    performanceContractDocsPositionDraft={
                        performanceContractDocsPositionDraft
                    }
                    isLoadingPerformanceDocsPositionDraft={
                        isLoadingPerformanceDocsPositionDraft
                    }
                    onPrepayment={(doc_prepayment) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            prepayment: doc_prepayment,
                        }))
                    }}
                    onDocNumber={(doc_number) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            performance_contract_doc_number: doc_number,
                        }))
                    }}
                    onDocDate={(doc_date) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            performance_contract_doc_date: doc_date,
                        }))
                    }}
                    onDocCode={(doc_code) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            performance_contract_doc_code: doc_code,
                        }))
                    }}
                    onDocName={(doc_name) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            performance_contract_doc_name: doc_name,
                        }))
                    }}
                    onChangeExchangeRate={(exchange_rate) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            exchange_rate: exchange_rate,
                        }))
                    }}
                    onChangeExchangeRateDate={(exchange_rate_date) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            exchange_rate_date: exchange_rate_date,
                        }))
                    }}
                    onChangeCurrency={(currency) => {
                        setPerformanceContractDocsPositionDraft((prev) => ({
                            ...prev,
                            currency_code: currency.code,
                            currency_digital_code: currency.digital_code,
                            currency_name: currency.name,
                        }))
                    }}
                    onAddDoc={(data) => {
                        createPerformanceContractDocNew(data)
                    }}
                    isSavingPerformanceDoc={isSavingPerformanceDoc}
                    //
                    onUpdateItem={onUpdateDraftItem}
                    onRemoveItems={onRemoveDraftItems}
                    onAddItems={onAddDraftItems}
                    contractGuid={props.contractGuid}
                    contractRegNumber={props.contractRegNumber}
                />
            )}

            {performanceDocsEditPopup && (
                <PerformanceDocsEditPopup
                    theme={props.theme}
                    performanceGuid={props.performanceGuid}
                    currencyList={props.currencyList}
                    handlePerformanceDocsEditPopup={(
                        performance_contract_doc_guid
                    ) =>
                        handlePerformanceDocsEditPopup(
                            performance_contract_doc_guid
                        )
                    }
                    performanceContractDocsPositionEdit={
                        performanceContractDocsPositionEdit
                    }
                    isLoadingPerformanceDocsPositionEdit={
                        isLoadingPerformanceDocsPositionEdit
                    }
                    onPrepayment={(doc_prepayment) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? { ...prev, prepayment: doc_prepayment }
                                : prev
                        )
                    }}
                    onDocNumber={(doc_number) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      performance_contract_doc_number:
                                          doc_number,
                                  }
                                : prev
                        )
                    }}
                    onDocDate={(doc_date) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      performance_contract_doc_date: doc_date,
                                  }
                                : prev
                        )
                    }}
                    onDocCode={(doc_code) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      performance_contract_doc_code: doc_code,
                                  }
                                : prev
                        )
                    }}
                    onDocName={(doc_name) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      performance_contract_doc_name: doc_name,
                                  }
                                : prev
                        )
                    }}
                    onChangeExchangeRate={(exchange_rate) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      exchange_rate: exchange_rate,
                                  }
                                : prev
                        )
                    }}
                    onChangeExchangeRateDate={(exchange_rate_date) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      exchange_rate_date: exchange_rate_date,
                                  }
                                : prev
                        )
                    }}
                    onChangeCurrency={(currency) => {
                        setPerformanceContractDocsPositionEdit((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      currency_code: currency.code,
                                      currency_digital_code:
                                          currency.digital_code,
                                      currency_name: currency.name,
                                  }
                                : prev
                        )
                    }}
                    onSaveDoc={(data) => {
                        savePerformanceContractDoc(data)
                    }}
                    isSavingPerformanceDoc={isSavingPerformanceDoc}
                    //
                    onUpdateItem={onUpdateEditItem}
                    onRemoveItems={onRemoveEditItems}
                    onAddItems={onAddEditItems}
                    contractGuid={props.contractGuid}
                    contractRegNumber={props.contractRegNumber}
                />
            )}

            {deletePerformancePositionPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000030] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документы?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeletePerformancePositionPopup
                                    }
                                    disabled={isDeleting ? true : false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Удалить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {deleteRowPerformancePositionPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#00000030] flex justify-center items-center z-50">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-col">
                                <label className="flex mb-1 text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Удалить документ?
                                </label>
                            </div>
                            <div className="flex w-full flex-row items-center justify-end gap-3">
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={
                                        handleDeleteRowPerformancePositionPopup
                                    }
                                    disabled={isDeleting ? true : false}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                                    onClick={handleDeleteClick}
                                >
                                    {isDeleting ? (
                                        <svg
                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            ></path>
                                        </svg>
                                    ) : (
                                        'Удалить'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default PerformanceDocTable
